import React from 'react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import styled from 'styled-components'
import { GlobalStyle } from './GlobalStyle.js'
import { HexMark, RingsMark } from '../components/Marks.js'

const Masthead = styled.header`
  background-color: var(--ink-deep);
  border-bottom: 1px solid var(--ink-rule);
`;

const MastheadInner = styled.div`
  max-width: 68rem;
  margin: 0 auto;
  padding: 1.6rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 1.15rem;

  @media (max-width: 34rem) {
    padding: 1.15rem 1.15rem;
    gap: 0.9rem;
  }
`;

/*
 * Both marks are white-on-navy standalone here — the masthead is a surface
 * they own, so neither wants its seal. `--ground` MUST track the actual
 * background (--ink-deep), because the rings' casing strokes are what cut
 * the over/under crossings; a mismatched casing haloes every crossing.
 */
const Mark = styled.span`
  --ground: var(--ink-deep);
  color: var(--card);
  display: flex;
  flex: none;
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.55));

  svg {
    display: block;
    width: 74px;
    height: 74px;
  }

  @media (max-width: 34rem) {
    svg {
      width: 46px;
      height: 46px;
    }
  }
`;

/* The game's mark, at the far end of the masthead. It carries its own
   name — unlabelled and this far from the wordmark, nothing tells you
   which of the two marks it is. */
const GameMark = styled(Mark)`
  margin-left: auto;
  padding-left: 1rem;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 62px;
    height: 62px;
  }

  span {
    font-family: var(--face-display);
    font-weight: 500;
    font-size: 0.62rem;
    text-transform: uppercase;
    letter-spacing: var(--engraved-tracking);
    color: var(--chalk-dim);
    /* the tracking adds a trailing gap; pull it back so it reads centred */
    text-indent: var(--engraved-tracking);
  }

  @media (max-width: 34rem) {
    svg {
      width: 40px;
      height: 40px;
    }
    span {
      font-size: 0.5rem;
      letter-spacing: 0.12em;
      text-indent: 0.12em;
    }
  }
`;

/* Engraved capitals — the display voice. The system names the wordmark
   explicitly, so this is Spectral 500 uppercase, not Cinzel. */
const Wordmark = styled.div`
  display: block;
  font-family: var(--face-display);
  font-weight: 500;
  font-size: 1.75rem;
  text-transform: uppercase;
  letter-spacing: var(--engraved-tracking);
  color: var(--vellum);
  line-height: 1.1;

  @media (max-width: 34rem) {
    font-size: 1.1rem;
    letter-spacing: 0.12em;
  }
`;

const Tagline = styled.div`
  font-family: var(--face-mono);
  font-size: 0.76rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--brass);
  margin-top: 0.45rem;

  @media (max-width: 34rem) {
    font-size: 0.64rem;
    letter-spacing: 0.1em;
  }
`;

const Footer = styled.footer`
  background-color: var(--ink-deep);
  border-top: 1px solid var(--ink-rule);
  margin-top: 3rem;
`;

const FooterInner = styled.div`
  max-width: 68rem;
  margin: 0 auto;
  padding: 2.5rem 1.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1.15rem 2.5rem;
  align-items: baseline;
  justify-content: space-between;
  font-family: var(--face-mono);
  font-size: 0.8rem;
  color: var(--chalk-dim);
`;

const FooterLinks = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
`;

const Header = () => (
  <Masthead>
    <div className="flagrule" />
    <MastheadInner>
      <Mark>
        <HexMark title="Pantera's Box" />
      </Mark>
      <div>
        <Wordmark>Pantera&rsquo;s Box</Wordmark>
        <Tagline>Home of Saxonberg MUD</Tagline>
      </div>
      <GameMark>
        <RingsMark id="masthead-rings" />
        <span>Saxonberg</span>
      </GameMark>
    </MastheadInner>
  </Masthead>
);

const TemplateWrapper = ({ children }) => (
  <div>
    <GlobalStyle />
    <Helmet
      title="Pantera's Box &mdash; Home of Saxonberg MUD"
      meta={[
        {
          name: 'description',
          content:
            'A text-first multiplayer world with an honest model underneath, governed by the people in it. Play the live demo at mud.panterasbox.com.',
        },
        { name: 'keywords', content: 'mud, saxonberg, text game, multiplayer, interactive fiction' },

        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: 'https://www.panterasbox.com/' },
        { property: 'og:site_name', content: "Pantera's Box" },
        { property: 'og:title', content: "Pantera's Box — Home of Saxonberg MUD" },
        {
          property: 'og:description',
          content:
            'A text-first multiplayer world with an honest model underneath, governed by the people in it. Play the live demo at mud.panterasbox.com.',
        },
        { property: 'og:image', content: 'https://www.panterasbox.com/og-card.png' },
        { property: 'og:image:type', content: 'image/png' },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:image:alt', content: "Pantera's Box — Home of Saxonberg MUD" },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:image', content: 'https://www.panterasbox.com/og-card.png' },
        { name: 'twitter:title', content: "Pantera's Box — Home of Saxonberg MUD" },
        {
          name: 'twitter:description',
          content:
            'A text-first multiplayer world with an honest model underneath, governed by the people in it.',
        },
      ]}
      link={[
        { rel: 'icon', href: '/favicon.ico', sizes: 'any' },
        { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
        { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
        { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: 'anonymous' },
        {
          rel: 'stylesheet',
          href:
            'https://fonts.googleapis.com/css2?family=Public+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Spectral:ital,wght@0,400;0,500;0,600;1,400&family=Newsreader:ital,wght@0,400;0,500;1,400&family=IBM+Plex+Mono:wght@400;500;600&display=swap',
        },
      ]}
    />
    <Header />
    <main>{children}</main>
    <Footer>
      <div className="flagrule" />
      <FooterInner>
        <span>Pantera&rsquo;s Box &mdash; streaming community, coder&rsquo;s workshop, game studio.</span>
        <FooterLinks>
          <li>
            <a href="https://mud.panterasbox.com">Play the demo</a>
          </li>
          <li>
            <a href="https://discord.gg/meEtHsZT8M">Discord</a>
          </li>
          <li>
            <a href="https://www.youtube.com/@BobbySchaetzle">YouTube</a>
          </li>
          <li>
            <a href="https://gitlab.com/panterasbox/saxonberg">Source</a>
          </li>
          <li>
            <a href="https://www.twitch.tv/bobalu_smallberries">Twitch</a>
          </li>
          <li>
            <a href="https://gitlab.com/bobalu">bobalu</a>
          </li>
        </FooterLinks>
      </FooterInner>
    </Footer>
  </div>
)

TemplateWrapper.propTypes = {
  children: PropTypes.node,
}

export default TemplateWrapper;
